import { TableOutlined } from '@ant-design/icons';
import { Descriptions, Tag } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import ToolWrapper from './components/ToolWrapper';

/**
 * Renders the ExploreThemes tool response.
 * Shows hierarchical theme exploration with counts and sentiment.
 */
function ExploreThemesTool({ item }) {
  const questionId = item.args?.question_id;
  const questionText = item.args?.question_text || '';
  const status = item.response?.status || '';
  const hierarchicalData = item.response?.themes || {};
  const isPending = item.response === null || item.response === undefined;

  const renderThemeTree = (themes, level = 0) => {
    return Object.entries(themes).map(([themeName, themeData]) => {
      const indent = level * 20;
      return (
        <div key={themeName} style={{ marginLeft: indent, marginBottom: 16 }}>
          <strong>{themeName}</strong>
          <div style={{ display: 'flex', gap: 16, marginTop: 4, flexWrap: 'wrap' }}>
            <Tag>Total: {themeData.count}</Tag>
            <Tag>Percent: {(themeData.percent * 100).toFixed(1)}%</Tag>
            <Tag color="green">Positive: {themeData.positive_count}</Tag>
            <Tag color="red">Negative: {themeData.negative_count}</Tag>
            <Tag>Neutral: {themeData.neutral_count}</Tag>
          </div>
          {themeData.description && (
            <div style={{ fontStyle: 'italic', color: '#666', marginTop: 4, fontSize: 12 }}>
              {themeData.description}
            </div>
          )}
          {themeData.children && renderThemeTree(themeData.children, level + 1)}
        </div>
      );
    });
  };

  return (
    <ToolWrapper toolName="Theme Exploration" isPending={isPending} icon={<TableOutlined />}>
      <Descriptions column={1} bordered size="small">
        {!isPending && (
          <Descriptions.Item label="Status">
            <Tag color={status === 'success' ? 'green' : 'red'}>{status}</Tag>
          </Descriptions.Item>
        )}
        {questionId && <Descriptions.Item label="Question ID">{questionId}</Descriptions.Item>}
        {questionText && (
          <Descriptions.Item label="Question Text">{questionText}</Descriptions.Item>
        )}
      </Descriptions>

      {hierarchicalData.question_text && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Question Information</h4>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{hierarchicalData.question_id}</Descriptions.Item>
            <Descriptions.Item label="Survey ID">{hierarchicalData.survey_id}</Descriptions.Item>
            <Descriptions.Item label="Text">{hierarchicalData.question_text}</Descriptions.Item>
          </Descriptions>
        </div>
      )}

      {hierarchicalData.themes && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ fontSize: 14, marginBottom: 8 }}>Theme Hierarchy</h4>
          {renderThemeTree(hierarchicalData.themes)}
        </div>
      )}
    </ToolWrapper>
  );
}

ExploreThemesTool.propTypes = {
  item: PropTypes.shape({
    args: PropTypes.shape({
      question_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      question_text: PropTypes.string,
    }),
    response: PropTypes.shape({
      status: PropTypes.string,
      question_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      question_text: PropTypes.string,
      survey_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      themes: PropTypes.objectOf(
        PropTypes.shape({
          count: PropTypes.number,
          percent: PropTypes.number,
          positive_count: PropTypes.number,
          negative_count: PropTypes.number,
          neutral_count: PropTypes.number,
          description: PropTypes.string,
          children: PropTypes.objectOf(PropTypes.shape({})),
        }),
      ),
    }),
  }).isRequired,
};

export default ExploreThemesTool;
